/**
 * El motor de las gráficas de línea del panel.
 *
 * Salió de `TrafficChart`, que era la única y estaba escrita alrededor de sus
 * dos métricas: «vistas» y «únicos» aparecían en el eje, en el tooltip y en el
 * texto para lectores de pantalla. Al llegar Analíticas de ventas hacían falta
 * las mismas rejillas, el mismo tooltip de dos series, la misma comparación a
 * rayas y la misma exploración con el dedo, pero con dinero — y copiar el
 * fichero habría dejado dos dibujos del mismo tipo que se irían separando, que
 * es justo lo que ya pasó una vez entre Analíticas y el resumen del panel.
 *
 * Así que aquí no hay ninguna métrica: hay una serie con un valor principal y
 * otro secundario, y quien la usa pone los nombres y el formato. `TrafficChart`
 * y `SalesChart` son dos envoltorios de diez líneas sobre esto.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { smoothChartPath } from '../../lib/chartPaths'
import { IconArrowDownRight, IconArrowUpRight, IconChartLine } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import { intlLocale } from '@webbuilder/i18n'
import { activeLanguage } from '../../lib/i18n'
import { translate } from '../../lib/i18n'

/** El mismo panel interior que usan las demás tarjetas del taller. */
const panel = {
  background: 'var(--dash-card-muted)',
  border: '1px solid',
  borderTopColor: 'var(--dash-card-muted-border-top)',
  borderRightColor: 'var(--dash-card-muted-border-side)',
  borderBottomColor: 'var(--dash-card-muted-border-bottom)',
  borderLeftColor: 'var(--dash-card-muted-border-side)',
  boxShadow: 'var(--dash-card-muted-shadow)',
  /* La esquina de las tarjetas del panel, como el `panel` de `analyticsShared`. */
  borderRadius: 'var(--dash-card-radius)',
} as const

/**
 * Un punto de la serie.
 *
 * `primary` es lo que dibuja la línea; `secondary` sólo sale en el tooltip. Es
 * el reparto que ya hacía tráfico —vistas en la línea, únicos al pasar por
 * encima— y el que evita que dos series con escalas distintas compitan en el
 * mismo eje.
 */
export type MetricPoint = { day: string; hour?: number; primary: number; secondary?: number }

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(intlLocale(activeLanguage()), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

const CHART_HEIGHT = 264
/**
 * Dentro de un widget del panel la gráfica comparte alto con las tarjetas de
 * cifras, así que va más baja y sin su propio marco: el widget ya es uno, y dos
 * bordes seguidos se leen como un doble filo.
 */
const COMPACT_HEIGHT = 168
const PAD = { top: 10, right: 14, bottom: 26, left: 44 }

/**
 * Rounds an axis maximum up to a readable step, keeping halves whole so the
 * middle gridline never reads like 67.5. The ladder is deliberately fine —
 * with only 1/2/5/10 steps a max of 134 would stretch the axis to 200 and
 * squash the line into the bottom two-thirds of the panel.
 */
const NICE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]

function niceMax(value: number): number {
  if (value <= 5) return 5
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = NICE_STEPS.find((candidate) => normalized <= candidate) ?? 10
  return step * magnitude
}

function tooltipDayLabel(day: string): string {
  const label = new Date(`${day}T00:00:00Z`).toLocaleDateString(intlLocale(activeLanguage()), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  })
  return label.charAt(0).toLocaleUpperCase() + label.slice(1)
}

/** Fecha corta para el prefijo de cada fila del tooltip: "19 jul". */
function tipRowDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(intlLocale(activeLanguage()), {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  })
}

/** Compact axis tick for the intraday chart, e.g. 9 → "9h". */
function hourLabel(hour: number): string {
  return `${hour}h`
}

/** Tooltip header for one hour, e.g. 9 → "09:00 – 10:00". */
function tooltipHourLabel(hour: number): string {
  const pad = (h: number) => String(h % 24).padStart(2, '0')
  return `${pad(hour)}:00 – ${pad(hour + 1)}:00`
}

/**
 * El cambio entre los dos puntos que el ratón está señalando.
 *
 * `trend` calla cuando no hay contra qué comparar o cuando el cambio es cero,
 * que es lo correcto para una insignia de tarjeta —ahí el silencio no estorba—.
 * Aquí no sirve: el tooltip acaba de enseñar dos cifras distintas y dejar el
 * hueco vacío parecería que falta un dato. Un guión dice "no se puede calcular"
 * mucho mejor que la nada.
 */
function PointDelta({ current, previous }: { current: number; previous: number }) {
  const delta = current - previous
  if (previous === 0) return <span className="dash-chart-tip-delta">—</span>
  const raw = Math.abs((delta / previous) * 100)
  const pct = raw >= 10 ? Math.round(raw) : Math.round(raw * 10) / 10
  return (
    <span className={`dash-chart-tip-delta${delta > 0 ? ' is-up' : delta < 0 ? ' is-down' : ''}`}>
      {delta > 0 ? <IconArrowUpRight size={11} /> : delta < 0 ? <IconArrowDownRight size={11} /> : null}
      {pct}%
    </span>
  )
}

const plain = (value: number) => value.toLocaleString()

/**
 * Una serie como línea, con su comparación a rayas por detrás y un tooltip que
 * sigue al puntero.
 *
 * Sized from a ResizeObserver rather than a scaled viewBox so strokes and text
 * stay true at any panel width.
 */
export function MetricChart({
  series,
  comparison = [],
  comparisonLabel = null,
  granularity,
  timezone,
  compact = false,
  loading = false,
  title,
  subtitle,
  icon,
  primaryLabel,
  secondaryLabel = null,
  formatPrimary = plain,
  formatSecondary = plain,
  formatAxis,
  ariaLabel,
  emptyLabel,
}: {
  series: MetricPoint[]
  /** Serie del tramo comparado, alineada por posición con `series`. */
  comparison?: MetricPoint[]
  comparisonLabel?: string | null
  granularity?: 'day' | 'hour' | undefined
  timezone?: string | undefined
  /** Versión para un widget del panel: más baja, sin marco ni cabecera. */
  compact?: boolean
  loading?: boolean
  title: string
  subtitle?: string
  icon?: ReactNode
  /** Nombre de la métrica que dibuja la línea. Encabeza su columna del tooltip. */
  primaryLabel: string
  /** `null` cuando la serie no trae segunda métrica: su columna desaparece. */
  secondaryLabel?: string | null
  formatPrimary?: (value: number) => string
  formatSecondary?: (value: number) => string
  /** Por defecto, el mismo formato de la métrica principal. */
  formatAxis?: (value: number) => string
  ariaLabel?: string
  /**
   * Qué decir cuando no hay ni un dato. Por defecto, una frase genérica.
   *
   * Vale la pena cambiarla cuando se sabe QUÉ falta —«todavía no has vendido
   * nada»— porque entonces deja de ser un aviso de que la pantalla está vacía y
   * pasa a decir algo del negocio.
   */
  emptyLabel?: string | undefined
}) {
  const { t } = useI18n()
  const hourly = granularity === 'hour'
  const wrapRef = useRef<HTMLDivElement>(null)
  const gradientId = useId()
  const [width, setWidth] = useState(0)
  /** Alto real del marco. Sólo se usa en modo compacto, donde la gráfica llena. */
  const [boxHeight, setBoxHeight] = useState(0)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [tipWidth, setTipWidth] = useState(0)
  /** Puntero capturado mientras se recorre la gráfica con dedo, lápiz o mouse. */
  const dragPointerRef = useRef<number | null>(null)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect
      if (!box) return
      setWidth(box.width)
      setBoxHeight(box.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const axisFormat = formatAxis ?? formatPrimary
  const showSecondary = secondaryLabel !== null

  // Puede tener otra longitud que la serie actual —junio 30 días, julio 31— así
  // que se dibuja sobre su propio reparto del ancho. Ambas representan "el
  // periodo entero", y es lo que las hace comparables de extremo a extremo.
  const showCompare = comparison.length > 1 && series.length > 1
  const yMax = niceMax(Math.max(
    ...series.map((point) => point.primary),
    ...(showCompare ? comparison.map((point) => point.primary) : []),
    0,
  ))
  const innerW = Math.max(width - PAD.left - PAD.right, 0)
  /**
   * En un widget la gráfica llena el hueco que le dejen.
   *
   * El alto sale de medir el marco, y el marco lo estira el sobrante de la
   * tarjeta — cuyo alto marca el widget más alto de su fila, no ésta. Sin esto
   * la gráfica se quedaba en 168px y el resto era blanco.
   *
   * No hay bucle porque en este modo el `svg` va en posición absoluta: dibuja
   * el alto que se le dice pero no empuja el marco, así que la medida no puede
   * alimentarse de sí misma. Con el `svg` en flujo, cada pasada lo habría hecho
   * un poco más alto.
   */
  const height = compact ? Math.max(COMPACT_HEIGHT, Math.round(boxHeight)) : CHART_HEIGHT
  const innerH = height - PAD.top - PAD.bottom
  const baseline = PAD.top + innerH

  const xAt = (i: number) =>
    series.length === 1 ? PAD.left + innerW / 2 : PAD.left + (i / (series.length - 1)) * innerW
  const yAt = (value: number) => PAD.top + innerH - (value / yMax) * innerH

  const points = series.map((point, i) => ({ ...point, cx: xAt(i), cy: yAt(point.primary) }))
  const linePath = smoothChartPath(points.map((point) => ({ x: point.cx, y: point.cy })))
  const areaPath = `${linePath} L ${xAt(series.length - 1).toFixed(2)} ${baseline} L ${xAt(0).toFixed(2)} ${baseline} Z`
  // Sin relleno y a trazos: tiene que leerse como el pasado, no competir con la
  // serie actual por la atención.
  const compareXAt = (i: number) =>
    comparison.length === 1 ? PAD.left + innerW / 2 : PAD.left + (i / (comparison.length - 1)) * innerW
  const comparePath = showCompare
    ? smoothChartPath(comparison.map((point, i) => ({ x: compareXAt(i), y: yAt(point.primary) })))
    : ''

  // Round the middle value itself, not just its label — otherwise a yMax of 15
  // draws the line at 7.5 and labels it "8".
  const gridValues = [...new Set([0, Math.round(yMax / 2), yMax])]
  // Hasta doce referencias uniformes. En anchos estrechos se reduce la cifra
  // automáticamente para que las fechas u horas nunca se monten entre sí.
  const labelCount = Math.min(series.length, 12, Math.max(3, Math.floor(innerW / 72)))
  const labelIndexes = series.length <= 1
    ? [0]
    : [...new Set(Array.from(
        { length: labelCount },
        (_, index) => Math.round((index / Math.max(labelCount - 1, 1)) * (series.length - 1)),
      ))]
  const active = hoverIndex === null ? null : points[hoverIndex]

  /**
   * El punto equivalente de la serie comparada.
   *
   * Por proporción y no por índice: los dos tramos pueden medir distinto —junio
   * 30 días, julio 31— y el índice 30 no existiría en el más corto. Lo que se
   * compara es "el mismo momento del periodo", que es lo que ambas líneas
   * representan en la misma posición horizontal.
   */
  const activeCompare = hoverIndex === null || !showCompare
    ? null
    : comparison[series.length === 1
        ? 0
        : Math.round((hoverIndex / (series.length - 1)) * (comparison.length - 1))] ?? null

  function updateHover(target: SVGRectElement, clientX: number) {
    if (series.length === 0) return
    const bounds = target.getBoundingClientRect()
    const ratio = bounds.width <= 0 ? 0 : (clientX - bounds.left) / bounds.width
    const index = Math.round(ratio * (series.length - 1))
    setHoverIndex(Math.min(Math.max(index, 0), series.length - 1))
  }

  function handlePointerDown(event: React.PointerEvent<SVGRectElement>) {
    dragPointerRef.current = event.pointerId
    event.currentTarget.setPointerCapture(event.pointerId)
    updateHover(event.currentTarget, event.clientX)
  }

  function handlePointerMove(event: React.PointerEvent<SVGRectElement>) {
    // El mouse conserva el hover habitual. Dedo y lápiz sólo exploran después
    // de tocar la gráfica, para no activar tooltips durante el scroll vertical.
    if (event.pointerType === 'mouse' || dragPointerRef.current === event.pointerId) {
      updateHover(event.currentTarget, event.clientX)
    }
  }

  function handlePointerUp(event: React.PointerEvent<SVGRectElement>) {
    if (dragPointerRef.current !== event.pointerId) return
    dragPointerRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  function handlePointerCancel(event: React.PointerEvent<SVGRectElement>) {
    if (dragPointerRef.current === event.pointerId) dragPointerRef.current = null
    setHoverIndex(null)
  }

  return (
    <div
      className={compact ? 'dash-traffic-chart is-compact' : 'dash-traffic-chart'}
      style={compact ? undefined : { ...panel, padding: 24, display: 'flex', flexDirection: 'column' }}
    >
      <div className="dash-analytics-chart-head">
        {/* En un widget la tarjeta ya tiene su propio título arriba; repetirlo
            aquí dejaba dos encabezados a cuatro píxeles uno de otro. */}
        {compact ? <span /> : (
          <div className="dash-analytics-card-heading">
            {icon ? <span className="dash-widget-glass-icon dash-stat-glass-icon" aria-hidden="true">{icon}</span> : null}
            <div>
              <h3 className="dash-card-title">{title}</h3>
              <span className="dash-card-meta">
                {subtitle}
                {/* Omitted when talking to a worker that predates the timezone field. */}
                {timezone ? ` · horario de ${timezone.replace(/_/g, ' ')}` : ''}
              </span>
            </div>
          </div>
        )}
        {/* Sin leyenda, el trazo a rayas es una línea gris sin explicación. */}
        {showCompare && comparisonLabel ? (
          <span className="dash-chart-legend">
            <span className="dash-chart-legend-item">
              <i className="dash-chart-swatch is-current" aria-hidden="true" />
              {t('chart.chosenPeriod')}
            </span>
            <span className="dash-chart-legend-item">
              <i className="dash-chart-swatch is-compare" aria-hidden="true" />
              {comparisonLabel}
            </span>
          </span>
        ) : null}
      </div>

      {/*
        El dibujo, en su propio recuadro dentro de la tarjeta.
        Sólo la zona de la línea: la cabecera —título, subtítulo y el tramo
        elegido— se queda fuera, porque es de la tarjeta y no del gráfico.
        Borde simple y sin fondo, que es lo que enmarca sin competir con la línea.
        En un widget no: allí la tarjeta ya es el marco y esto sería uno dentro
        de otro.
      */}
      <div
        ref={wrapRef}
        style={compact
          ? { position: 'relative', width: '100%', flex: '1 1 auto', minHeight: COMPACT_HEIGHT }
          : {
            /*
             * Sin alto propio: lo pone el dibujo que lleva dentro.
             *
             * Fijarlo obligaba a decidir si esos píxeles eran de contenido o de
             * caja, y las dos salidas fallaban: con `content-box` el ancho pasaba
             * a ser «100% del padre MÁS el relleno» y la línea se salía por la
             * derecha; con `border-box` el relleno se comía el alto del svg y
             * desbordaba por abajo. Dejando que la caja se ajuste al contenido,
             * el alto sale solo —dibujo más relleno— y el ancho vuelve a ser el
             * de siempre, que es el que mide el observador.
             */
            position: 'relative',
            width: '100%',
            padding: '18px 16px',
            border: '1px solid var(--dash-border)',
            borderRadius: 12,
          }}
      >
        {loading ? (
          <div className="sk dash-analytics-chart-skeleton" aria-hidden="true">
            <span /><span /><span />
          </div>
        ) : series.length === 0 ? (
          /*
            Sin un solo dato no se dibuja la rejilla.

            Las tres rayas con su 0, su 3 y su 5 son la escala de unos números
            que no existen: prometen una gráfica, y lo que hay es un recuadro
            vacío con marcas. Peor todavía, el techo del eje sale de un máximo
            inventado, así que esas cifras no significan nada.

            En su lugar, una frase que dice por qué está vacío. Ocupa el mismo
            alto que ocuparía el dibujo para que la tarjeta no dé un salto en
            cuanto entre el primer dato.
          */
          <div className="dash-analytics-chart-empty" style={compact ? undefined : { height: CHART_HEIGHT }}>
            <IconChartLine size={compact ? 18 : 22} aria-hidden="true" />
            <p>{emptyLabel ?? t('chart.empty')}</p>
          </div>
        ) : width > 0 ? (
          <svg
            className="dash-analytics-content-in"
            width={width}
            height={height}
            role="img"
            /* Por defecto, lo mismo que dice la cabecera más el techo del eje:
               quien no ve el dibujo necesita saber sobre qué escala está. */
            aria-label={ariaLabel ?? translate('chart.aria', { what: [title, subtitle].filter(Boolean).join('. '), max: axisFormat(yMax) })}
            /* En modo compacto sale del flujo: dibuja el alto que se le pasa
               sin empujar el marco, que es lo que rompería la medida. */
            style={compact
              ? { display: 'block', overflow: 'visible', position: 'absolute', inset: 0 }
              : { display: 'block', overflow: 'visible' }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--dash-analytics-ink)" stopOpacity="0.2" />
                <stop offset="100%" stopColor="var(--dash-analytics-ink)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Recessive grid: three lines, labelled on the left. */}
            {gridValues.map((value) => (
              <g key={value}>
                <line
                  x1={PAD.left}
                  y1={yAt(value)}
                  x2={PAD.left + innerW}
                  y2={yAt(value)}
                  stroke="var(--dash-border)"
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 10}
                  y={yAt(value)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="var(--dash-muted)"
                  fontSize={11}
                >
                  {axisFormat(value)}
                </text>
              </g>
            ))}

            {series.length > 1 ? (
              <>
                <path d={areaPath} fill={`url(#${gradientId})`} />
                {comparePath ? (
                  <path
                    className="dash-analytics-compare-line"
                    d={comparePath}
                    fill="none"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
                <path
                  d={linePath}
                  fill="none"
                  stroke="var(--dash-analytics-ink)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            ) : null}

            {/* A lone day has no line to draw — show the point itself. */}
            {series.length === 1 && points[0] ? (
              <circle cx={points[0].cx} cy={points[0].cy} r={4} fill="var(--dash-analytics-ink)" />
            ) : null}

            {active ? (
              <>
                <line
                  x1={active.cx}
                  y1={PAD.top}
                  x2={active.cx}
                  y2={baseline}
                  stroke="var(--dash-border-strong)"
                  strokeWidth={1}
                />
                {/* El punto de la serie comparada va primero para quedar por
                    debajo del actual si ambos caen en el mismo sitio. Hueco y
                    atenuado, como su línea. */}
                {activeCompare ? (
                  <circle
                    className="dash-analytics-compare-dot"
                    cx={active.cx}
                    cy={yAt(activeCompare.primary)}
                    r={3.5}
                    fill="var(--dash-bg)"
                    strokeWidth={1.5}
                  />
                ) : null}
                <circle
                  cx={active.cx}
                  cy={active.cy}
                  r={4.5}
                  fill="var(--dash-analytics-ink)"
                  stroke="var(--dash-bg)"
                  strokeWidth={2}
                />
              </>
            ) : null}

            {labelIndexes.map((index) => {
              const point = points[index]
              if (!point) return null
              const anchor = index === 0 ? 'start' : index === series.length - 1 ? 'end' : 'middle'
              return (
                <text
                  key={index}
                  x={point.cx}
                  y={baseline + 16}
                  textAnchor={series.length === 1 ? 'middle' : anchor}
                  fill="var(--dash-muted)"
                  fontSize={11}
                >
                  {hourly ? hourLabel(point.hour ?? index) : dayLabel(point.day)}
                </text>
              )
            })}

            {/* Hit area spans the full plot height so the crosshair is easy to catch. */}
            <rect
              x={PAD.left}
              y={PAD.top}
              width={innerW}
              height={innerH}
              fill="transparent"
              style={{ touchAction: 'pan-y', cursor: 'crosshair' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
              onPointerLeave={() => {
                if (dragPointerRef.current === null) setHoverIndex(null)
              }}
            />
          </svg>
        ) : null}

        {!loading && active ? (
          <div
            /* Se mide al montarse para saber cuánto hay que apartarlo del
               borde. Con un margen fijo, un tooltip ancho seguía saliéndose y
               uno estrecho se quedaba lejos del punto sin necesidad. */
            ref={(node) => { if (node) setTipWidth(node.offsetWidth) }}
            style={{
              position: 'absolute',
              left: Math.min(
                Math.max(active.cx, tipWidth / 2),
                Math.max(width - tipWidth / 2, tipWidth / 2),
              ),
              top: Math.max(active.cy - 12, 0),
              transform: 'translate(-50%, -100%)',
            }}
            /* `is-hourly` estrecha la rejilla a cuatro columnas, que es lo que
               ocupa una sola métrica. Se aplica también cuando no hay segunda
               métrica aunque el tramo sea de varios días. */
            className={`dash-card-meta dash-chart-tip${showSecondary ? '' : ' is-hourly'}${width < 360 ? ' is-narrow' : ''}`}
          >
            {/* El encabezado identifica el punto bajo el cursor. El rango ya
                está visible en el selector y repetirlo aquí hacía parecer que
                el popup correspondía al tramo entero, no al día activo. */}
            <div className="dash-chart-tip-day">
              {hourly
                ? tooltipHourLabel(active.hour ?? 0)
                : tooltipDayLabel(active.day)}
            </div>

            {/* Los nombres de las métricas van una sola vez, como cabecera de
                columna, en vez de repetirse en cada fila. Es lo que deja las
                cifras alineadas en vertical y comparables de un vistazo.
                `is-views`/`is-uniques` son nombres de hueco en la rejilla, no
                de métrica: colocan la primera y la segunda columna. */}
            <span className="dash-chart-tip-head is-views">{primaryLabel}</span>
            {showSecondary ? <span className="dash-chart-tip-head is-uniques">{secondaryLabel}</span> : null}

            {/* Cada fila lleva delante el mismo trazo que su línea en la
                gráfica: con dos filas de cifras del mismo formato no hay forma
                de deducir cuál es cuál. */}
            <i className="dash-chart-swatch is-current" aria-hidden="true" />
            <span className="dash-chart-tip-rowday">
              {hourly ? 'Ahora' : tipRowDay(active.day)}
            </span>
            {/* Número y porcentaje en celdas separadas: dentro de la misma, el
                porcentaje empujaba al número de la fila de arriba y el de abajo
                se quedaba solo contra el borde, así que no coincidían. */}
            <span className="dash-chart-tip-num is-views">{formatPrimary(active.primary)}</span>
            <span className="dash-chart-tip-pct is-views">
              {activeCompare ? <PointDelta current={active.primary} previous={activeCompare.primary} /> : null}
            </span>
            {showSecondary ? (
              <>
                <span className="dash-chart-tip-num is-uniques">{formatSecondary(active.secondary ?? 0)}</span>
                <span className="dash-chart-tip-pct is-uniques">
                  {activeCompare ? <PointDelta current={active.secondary ?? 0} previous={activeCompare.secondary ?? 0} /> : null}
                </span>
              </>
            ) : null}

            {activeCompare ? (
              <>
                <i className="dash-chart-swatch is-compare" aria-hidden="true" />
                <span className="dash-chart-tip-rowday is-compare">{tipRowDay(activeCompare.day)}</span>
                <span className="dash-chart-tip-num is-views is-compare">{formatPrimary(activeCompare.primary)}</span>
                <span className="dash-chart-tip-pct is-views is-compare" />
                {showSecondary ? (
                  <>
                    <span className="dash-chart-tip-num is-uniques is-compare">{formatSecondary(activeCompare.secondary ?? 0)}</span>
                    <span className="dash-chart-tip-pct is-uniques is-compare" />
                  </>
                ) : null}
              </>
            ) : null}

          </div>
        ) : null}
      </div>
    </div>
  )
}
